import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	OptionsToProducts,
	SelectFilterOption,
	SelectFilterOptionChange,
	SelectFilterViewState,
} from '../../../common/constants/filters/select-filter/types';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../common/constants/product-keys';
import { SearchExperience } from '../../../common/constants/search-experience';
import { useSelectFilterStore } from '../../store/search';
import { typeLabels } from '../../store/search/filters/universal-type/messages';
import { getProductToSelect } from '../utils';
import { getRelevantProductsForUniversalFilters } from './get-relevant-products-for-universal-filters';

export const useSelectFilter: ({
	filterId,
	optionValuesToExclude,
}: {
	filterId: string;
	optionValuesToExclude?: string[];
}) => [
	SelectFilterViewState | undefined,
	{
		setOption: (
			option: SelectFilterOption | SelectFilterOption[],
			customValue?: Record<string, string>,
			setSelectedProduct?: ProductKeys,
			validateDependentFilterId?: string,
		) => void | Promise<void>;
		loadInitialOptions: () => void | Promise<void>;
		setLookupInput: (query: string) => void | Promise<void>;
		clearLookupInput: () => void | Promise<void>;
		clearOptions: (dependentFilterId?: string) => void | Promise<void>;
	},
] = ({
	filterId,
	optionValuesToExclude,
}: {
	filterId: string;
	optionValuesToExclude?: string[];
}): [SelectFilterViewState | undefined, typeof viewActions] => {
	const intl = useIntl();
	const [state, storeActions] = useSelectFilterStore({
		filterId,
	});
	const isContributor = filterId === 'contributor';

	const viewActions = {
		setOption: (
			option: SelectFilterOption | SelectFilterOption[],
			customValue?: Record<string, string>,
			setSelectedProduct?: ProductKeys,
			validateDependentFilterId?: string,
		) => {
			const options = Array.isArray(option) ? option : [option];
			const optionChange = options.map(
				(option) =>
					({
						value: option.value,
						isSelected: !option.isSelected,
						// Preserve display fields for client-only options (see SelectFilterOptionChange)
						// so a mined deactivated contributor keeps its label/avatar/metadata once the
						// store persists it.
						...(isContributor
							? {
									label: option.label,
									avatarUrl: option.avatarUrl,
									trackingKey: option.trackingKey,
									queryParamValue: option.queryParamValue,
									metadata: option.metadata,
								}
							: {}),
					}) satisfies SelectFilterOptionChange,
			);

			const productToSelect = getProductToSelect({
				isUniversal: state?.universal,
				primaryProduct: state?.primaryProduct ?? undefined,
				productToSet: setSelectedProduct,
				selectedProduct: state?.selectedProducts[0],
				experience: state?.experience,
			});

			const customOptions = {
				customValue,
				setSelectedProduct: productToSelect,
				validateDependentFilterId,
			};

			return storeActions.setSelectFilterOption({
				filterId,
				optionChange,
				...(Object.values(customOptions).some((customOption) => customOption !== undefined)
					? { options: customOptions }
					: {}),
			});
		},
		loadInitialOptions: () => storeActions.loadInitialSelectFilterOptions({ intl, filterId }),
		setLookupInput: (query: string) => storeActions.setSelectFilterLookupInput({ filterId, query }),
		clearLookupInput: () => storeActions.setSelectFilterLookupInput({ filterId, query: '' }),
		clearOptions: (dependentFilterId?: string) =>
			storeActions.clearSelectFilterOptions({ filterId, dependentFilterId }),
	};

	if (!state) {
		return [, viewActions] satisfies [SelectFilterViewState | undefined, typeof viewActions];
	}

	const filteredOptions = state.options.filter((option) => {
		if (optionValuesToExclude) {
			return !optionValuesToExclude.includes(option.value);
		}
		return true;
	});

	const selectedOptionsCount = state.selectedOptions.length;

	const viewState = {
		defaultIsOpen: state.alwaysVisible === false,
		isLookupDefined: state.lookup.isDefined,
		isLoading:
			state.queryParams.isLoading || state.initialOptions.isLoading || state.lookup.isLoading,
		lookupInput: state.lookup.input,
		options: filteredOptions,
		suggestedOptions: filteredOptions.filter((option) => !option.isSelected && option.isSuggested),
		selectedOptions: state.selectedOptions,
		isLookupActive: state.lookup.isActive,
		alwaysVisible: state.alwaysVisible,
		customValue: state.customValue,
		badgeLabel: selectedOptionsCount > 1 ? `+${selectedOptionsCount - 1}` : undefined,
		firstSelectedOptionLabel: state.selectedOptions[0]?.label,
	} satisfies SelectFilterViewState;

	return [viewState, viewActions] satisfies [SelectFilterViewState, typeof viewActions];
};

// This is a select filter which has access to the selected product state
export const useUniversalSelectFilter: ({
	filterId,
	filterOptionsByPrimaryProduct,
	optionsToProducts,
	selectPrimaryProductOnChange,
}: {
	filterId: string;
	filterOptionsByPrimaryProduct?: boolean;
	optionsToProducts: OptionsToProducts;
	selectPrimaryProductOnChange?: boolean;
}) => [
	SelectFilterViewState | undefined,
	{
		setOption: (option: SelectFilterOption | SelectFilterOption[]) => void | Promise<void>;
		loadInitialOptions: () => void | Promise<void>;
		setLookupInput: (query: string) => void | Promise<void>;
		clearLookupInput: () => void | Promise<void>;
		clearOptions: () => void | Promise<void>;
	},
] = ({
	filterId,
	filterOptionsByPrimaryProduct = true,
	optionsToProducts,
	selectPrimaryProductOnChange = true,
}: {
	filterId: string;
	filterOptionsByPrimaryProduct?: boolean;
	optionsToProducts: OptionsToProducts;
	selectPrimaryProductOnChange?: boolean;
}): [SelectFilterViewState | undefined, typeof viewActions] => {
	const intl = useIntl();
	const [state, storeActions] = useSelectFilterStore({
		filterId,
	});

	const viewActions = {
		setOption: (option: SelectFilterOption | SelectFilterOption[]) => {
			const options = Array.isArray(option) ? option : [option];
			const optionChange = options.map(
				(option) =>
					({
						value: option.value,
						isSelected: !option.isSelected,
					}) satisfies SelectFilterOptionChange,
			);

			const productToSelect = selectPrimaryProductOnChange
				? getProductToSelect({
						isUniversal: state?.universal,
						primaryProduct: state?.primaryProduct ?? undefined,
						selectedProduct: state?.selectedProducts[0],
						experience: state?.experience,
					})
				: undefined;

			return storeActions.setSelectFilterOption({
				filterId,
				optionChange,
				options: { setSelectedProduct: productToSelect },
			});
		},
		loadInitialOptions: () => storeActions.loadInitialSelectFilterOptions({ filterId, intl }),
		setLookupInput: (query: string) => storeActions.setSelectFilterLookupInput({ filterId, query }),
		clearLookupInput: () => storeActions.setSelectFilterLookupInput({ filterId, query: '' }),
		clearOptions: () => storeActions.clearSelectFilterOptions({ filterId }),
	};

	if (!state) {
		return [, viewActions] satisfies [SelectFilterViewState | undefined, typeof viewActions];
	}

	const selectedProductsAvailable = state.selectedProducts.length > 0;
	const productsNew = getRelevantProductsForUniversalFilters({
		selectedProducts: state.selectedProducts,
		primaryProduct: filterOptionsByPrimaryProduct ? (state.primaryProduct ?? undefined) : undefined,
		experience: state.experience,
		availableProducts: state.availableProducts,
	});

	const products = productsNew;
	const isAtlasSelected = state.selectedProducts[0] === ProductKeys.Atlas;
	const isLoomContext =
		state.selectedProducts[0] === ProductKeys.Loom ||
		(state.selectedProducts.length === 0 &&
			state.experience === SearchExperience.QUICK_FIND &&
			state.primaryProduct === ProductKeys.Loom);

	const filteredOptions = state.options
		.filter(
			(option) =>
				option.isDefault ||
				option.isSelected ||
				optionsToProducts[option.value]?.some((product) => products.includes(product)),
		)
		.filter((option) => {
			// TODO: Product-specific filtering in this shared controller is a code smell.
			// Atlas introduced this workaround to only show Goal and Tag types when Atlas is selected.
			// Loom had to alter this because we need to see Tag type when Loom is selected.
			if (option.value === 'goal') {
				return isAtlasSelected;
			}

			if (option.value === 'tag') {
				return isAtlasSelected || isLoomContext;
			}

			return true;
		})
		// Filtering out all options that are tied exclusively to allFilterExcludedProducts for universal types
		.filter((option) =>
			!selectedProductsAvailable && optionsToProducts[option.value]?.length
				? optionsToProducts[option.value].some(
						(product) => !state.allFilterExcludedProducts.includes(product),
					)
				: true,
		);

	const selectedOptions = state.selectedOptions.map((option) => ({ ...option }));

	if (
		products.length === 1 &&
		products[0] === ProductKeys.ConfluenceDC &&
		fg('confluence_live_pages_open_beta_trait_opted_in')
	) {
		const isPageOption = (option: SelectFilterOption) => option.value === TypeFilterValueKey.Page;

		// Update both filtered and selected options that match Page type
		[filteredOptions, selectedOptions]
			.flatMap((options) => options.filter(isPageOption))
			.forEach((option) => {
				option.label = intl.formatMessage(typeLabels.page);
			});
	}

	const viewState = {
		defaultIsOpen: state.alwaysVisible === false,
		isLookupDefined: state.lookup.isDefined,
		isLoading:
			state.queryParams.isLoading || state.initialOptions.isLoading || state.lookup.isLoading,
		lookupInput: state.lookup.input,
		options: filteredOptions,
		suggestedOptions: filteredOptions.filter((option) => !option.isSelected && option.isSuggested),
		selectedOptions,
		isLookupActive: state.lookup.isActive,
		alwaysVisible: state.alwaysVisible,
	} satisfies SelectFilterViewState;

	return [viewState, viewActions] satisfies [SelectFilterViewState, typeof viewActions];
};
